import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePrevious } from '../../../../../../hooks/usePrevious';
import { Guid } from '../../../../../../store/model/guid';
import { WidgetDataRepository } from '../../../../../../store/repository/widgets/widgetDataRepository';
import { anyAssetUpdated } from './anyAssetUpdated';
import { ignorePromise, Option, Options, WidgetType } from '@ael/core-api';

type Dimensions = { width: number; height: number };
export type QuestionAssetData = {
  url: string;
  type: 'image';
  dimensions: Dimensions | null;
};

export type GetAssetDataForOption = (optionIndex: number) => QuestionAssetData | null;
export type GetAssetDataForQuestionTitle = () => QuestionAssetData | null;

type AssetUrls = ReadonlyArray<string | null>;
type AssetDimensions = ReadonlyArray<Dimensions | null>;

type QuestionWidgetInitData = {
  assetsUrls: Array<string | null>;
  assetsDimensions: Array<Dimensions | null>;
};

type QuestionWidgetAssets = {
  assetUrls: AssetUrls;
  assetDimensions: AssetDimensions;
};

export const useQuestionWidgetAssetProvider = (widgetId: Guid, options: Options, titleAssetId: string | null) => {
  const assetIds = useMemo(() => {
    const assetIds = options.map((option: Option) => option.assetId ?? null);
    assetIds.push(titleAssetId);
    return assetIds;
  }, [options, titleAssetId]);

  const [data, setData] = useState<QuestionWidgetAssets>({ assetUrls: [], assetDimensions: [] });
  const prevAssetIds = usePrevious(assetIds);
  const clearData = () => {
    setData({ assetUrls: [], assetDimensions: [] });
  };
  const fetchAssetData = useCallback(async () => {
    await WidgetDataRepository.getWidgetData({ assets: assetIds }, WidgetType.question, widgetId).then(
      (data: unknown) => {
        const typedData = data as QuestionWidgetInitData;
        setData({
          assetUrls: typedData.assetsUrls,
          assetDimensions: typedData.assetsDimensions,
        });
      }
    );
  }, [assetIds, widgetId]);

  useEffect(() => {
    if (anyAssetUpdated(prevAssetIds, assetIds)) {
      clearData();
      ignorePromise(fetchAssetData());
    }
  }, [fetchAssetData, prevAssetIds, assetIds]);

  const getAssetDataForOption: GetAssetDataForOption = useCallback(
    (optionIndex: number) => {
      const url = data.assetUrls[optionIndex];
      if (!url) {
        return null;
      }
      return { url: url, type: 'image', dimensions: data.assetDimensions[optionIndex] ?? null };
    },
    [data]
  );
  const getAssetDataForQuestionTitle: GetAssetDataForQuestionTitle = useCallback(() => {
    const url = data.assetUrls[options.length]; // last index
    if (!url) {
      return null;
    }
    return {
      url: url,
      type: 'image',
      dimensions: data.assetDimensions[options.length],
    };
  }, [data, options.length]);

  return { getAssetDataForOption, getAssetDataForQuestionTitle };
};
